import { Field, Label, Switch } from '@headlessui/react';
import { cn } from '@theprintbar/shared';
import { ReactNode } from 'react';

interface ToggleProps {
	enabled: boolean;
	onChange: (enabled: boolean) => void;
	className?: string;
	disabledIcon?: any;
	label?: ReactNode;
	labelClassName?: string;
	labelPosition?: 'left' | 'right';
	enabledIcon?: any;
	enabledClassName?: string;
	disabledClassName?: string;
	disabledIconClassName?: string;
	name?: string;
	value?: string;
	small?: boolean;
	innerLabel?: boolean;
	disabled?: boolean;
	type?: 'button' | 'submit' | 'reset';
}

export function Toggle({
	enabled,
	onChange,
	type,
	label,
	labelPosition = 'left',
	labelClassName,
	enabledClassName,
	disabledClassName,
	name,
	value,
	className,
	disabledIcon: DIcon,
	enabledIcon: EIcon,
	disabledIconClassName,
	small,
	disabled,
	innerLabel = false,
}: ToggleProps) {
	const LabelEl = label ? (
		<Label className={cn(enabled ? 'text-blue' : 'text-grey', 'lg:text-base text-sm hover:cursor-pointer', labelClassName)}>
			{label}
		</Label>
	) : null;

	const SwitchObj = (
		<Switch
			as="button"
			checked={enabled}
			onChange={onChange}
			disabled={disabled}
			type={type}
			name={name}
			value={value}
			className={cn(
				enabled ? 'bg-blue border border-blue text-blue' : 'bg-grey-light border border-grey text-grey',
				'relative inline-flex flex-shrink-0 items-center cursor-pointer rounded-full transition-all ease-in-out',
				small ? 'h-6 w-12 px-1' : 'h-7 w-16 px-1',
				className,
				enabled && enabledClassName,
				!enabled && disabledClassName,
			)}
		>
			<span className="sr-only">Use setting</span>
			{innerLabel && (
				<span
					className={cn(
						'absolute transition duration-200 ease-in-out text-xs text-inherit uppercase',
						enabled ? 'translate-x-0 ml-0' : 'translate-x-6 mr-1',
					)}
				>
					{enabled ? 'On' : 'Off'}
				</span>
			)}
			<span
				className={cn(
					enabled ? 'translate-x-8 bg-white' : 'translate-x-0 bg-grey',
					'pointer-events-none relative inline-block  aspect-square transform rounded-full ring-0 transition duration-200 ease-in-out',
					small ? ' h-4 w-4' : 'h-5 w-5',
				)}
			>
				<span
					className={cn(
						enabled ? 'opacity-0 duration-100 ease-out' : 'opacity-100 duration-200 ease-in',
						'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
					)}
					aria-hidden="true"
				>
					{DIcon && (
						<DIcon className={cn('text-white-pearl/60', small ? 'h-4 w-4' : 'h-5 w-5', !enabled && disabledIconClassName)} />
					)}
				</span>
				<span
					className={cn(
						enabled ? 'opacity-100 duration-200 ease-in' : 'opacity-0 duration-100 ease-out',
						'absolute inset-0 flex h-full w-full items-center justify-center transition-opacity',
					)}
					aria-hidden="true"
				>
					{EIcon && <EIcon className={cn('text-blue', small ? 'h-4 w-4' : 'h-5 w-5')} />}
				</span>
			</span>
		</Switch>
	);

	return LabelEl ? (
		<Field as="div" className="flex items-center space-x-2">
			{labelPosition === 'left' && LabelEl}
			{SwitchObj}
			{labelPosition === 'right' && LabelEl}
		</Field>
	) : (
		SwitchObj
	);
}
